import { useState, type CSSProperties } from 'react';
import type { ClientRoomState } from '@hitster/shared';
import { playerColor } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import { Tokens } from './common.tsx';
import { IconChevron, IconClose, IconCrown, IconNote } from './Icon.tsx';
import { Timeline } from './Timeline.tsx';

/**
 * Spielerliste mit Kartenstand, Token und Rollen.
 * Wird in Lobby, Steuerung und TV-Ansicht gleichermassen benutzt.
 */
export function PlayerList({
  state,
  onKick,
  onRemoveLocal,
  showKick = false,
  expandable = false,
}: {
  state: ClientRoomState;
  onKick?: (playerId: string) => void;
  /**
   * Mitspieler am Host-Geraet werden nicht "gekickt", sondern entfernt --
   * sie haben keine eigene Verbindung, die man trennen koennte.
   */
  onRemoveLocal?: (playerId: string) => void;
  showKick?: boolean;
  /**
   * Zeitleiste je Spieler aufklappbar machen.
   *
   * Fuer die Steuerung am Handy: dort sieht man sonst nur die eigene
   * Leiste und die des aktiven Spielers. Wer einschaetzen will, ob sich
   * ein Klau lohnt, muss aber wissen, wie die anderen stehen.
   *
   * In der Fernsehansicht aus: dort stehen ohnehin alle Leisten
   * untereinander, ein zweites Mal waere Doppelung.
   */
  expandable?: boolean;
}) {
  const t = useT();
  const inGame = state.phase !== 'lobby';

  /** Nur eine Leiste gleichzeitig -- sonst wird die Liste unuebersichtlich. */
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="stack stack--tight">
      {state.players.map((player) => {
        const isActive = player.id === state.activePlayerId && inGame;
        const isWinner = state.winnerIds.includes(player.id);
        // Ohne Karten gibt es nichts aufzuklappen.
        const canExpand = expandable && inGame && player.timeline.length > 0;
        const isExpanded = canExpand && expandedId === player.id;

        return (
          <div
            key={player.id}
            /* Aus der ID abgeleitet -- ueberlebt Neuverbinden. */
            style={{ '--player': playerColor(player.id) } as CSSProperties}
          >
          <div
            className={[
              'player-row',
              isActive ? 'player-row--active' : '',
              player.connected ? '' : 'player-row--disconnected',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className="min-w-0" style={{ flex: 1 }}>
              <div className="row" style={{ gap: '0.375rem' }}>
                {/*
                 * Farbpunkt statt gefaerbtem Namen: eingefaerbter Text
                 * verliert auf dunklem Grund an Lesbarkeit, und bei
                 * sechs Spielern konkurrieren sechs Textfarben
                 * miteinander. Ein Punkt ordnet zu, ohne zu stoeren.
                 */}
                <span className="player-row__dot" aria-hidden="true" />
                <span className="player-row__name">{player.name}</span>
                {player.id === state.youId && <span className="badge badge--you">{t('common.you')}</span>}
                {player.isHost && <span className="badge badge--host">{t('common.host')}</span>}
                {player.isAudioDevice && (
                  <span className="badge badge--speaker" title={t('audio.active')}>
                    <IconNote size="0.85em" />
                  </span>
                )}
                {isWinner && (
                  <span className="badge badge--premium">
                    <IconCrown size="0.85em" />
                  </span>
                )}
                {player.isLocal && <span className="badge">{t('local.badge')}</span>}
              </div>

              {inGame && (
                <div className="player-row__meta">
                  {t('game.cards', {
                    count: player.timeline.length,
                    target: state.settings.targetCards,
                  })}
                </div>
              )}
              {!inGame && player.spotifyLinked && (
                <div className="player-row__meta">
                  {player.hasPremium
                    ? `Spotify · ${player.spotifyDisplayName ?? ''}`
                    : t('spotify.notPremium', { product: '—' })}
                </div>
              )}
            </div>

            {inGame && state.settings.maxTokens > 0 && (
              <Tokens count={player.tokens} max={state.settings.maxTokens} />
            )}

            {canExpand && (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setExpandedId(isExpanded ? null : player.id)}
                aria-expanded={isExpanded}
                aria-label={t('game.showTimelineOf', { name: player.name })}
                title={t('game.showTimelineOf', { name: player.name })}
              >
                <IconChevron up={isExpanded} />
              </button>
            )}

            {showKick && !player.isHost && (onKick || onRemoveLocal) && (
              <button
                type="button"
                className="btn btn--ghost btn--danger"
                onClick={() =>
                  player.isLocal ? onRemoveLocal?.(player.id) : onKick?.(player.id)
                }
                aria-label={`${t('lobby.kick')} ${player.name}`}
              >
                <IconClose />
              </button>
            )}
          </div>

          {isExpanded && (
            <div className="player-row__timeline">
              <Timeline cards={player.timeline} ownerId={player.id} compact />
            </div>
          )}
          </div>
        );
      })}
    </div>
  );
}
